import { Transform } from "class-transformer"
import {
    IsEnum,
    IsIn,
    IsNotEmpty,
    IsOptional,
    IsString,
    Matches,
} from "class-validator"
import { usaStates } from "typed-usa-states"

import {
    LegalGender,
    PayerCoverageCheckNetwork,
} from "@common-shared/config/coverage.config"
import { CoveragePatientType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PolicyModalityCode } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"

import { FRONT_REGEX_CONFIG } from "@front-shared/config/regex.config"

class CommonCustomerDataDTO {
    // To handle empty strings in CSV for patient type column
    @Transform(({ value }) => value || undefined)
    @IsOptional()
    @IsEnum(CoveragePatientType)
    customerPatientType?: CoveragePatientType

    @IsOptional()
    @IsString()
    customerPatientId?: string

    @IsOptional()
    @IsString()
    customerPatientNextAppointmentDate?: string
}

class CommonProviderMemberDataDTO extends CommonCustomerDataDTO {
    @IsNotEmpty({
        message: "Specialty should not be empty",
    })
    @IsEnum(PolicyModalityCode, {
        message: `Service must be one of the following values: ${Object.values(
            PolicyModalityCode,
        ).join(", ")}`,
    })
    modalityCode: PolicyModalityCode

    @IsNotEmpty({
        message: "Provider NPI should not be empty",
    })
    @IsString()
    providerNpi: string

    @IsNotEmpty({
        message: "First Name should not be empty",
    })
    @IsString()
    firstName: string

    @IsNotEmpty({
        message: "Last Name should not be empty",
    })
    @IsString()
    lastName: string

    @IsNotEmpty({
        message: "Member DOB should not be empty",
    })
    @IsString()
    dob: string

    @IsOptional()
    @IsString()
    officeId?: string
}

class CommonContinuousMonitoringInputDTO extends CommonProviderMemberDataDTO {
    @IsOptional()
    @IsString()
    cptCode?: string

    @IsNotEmpty({
        message: "Provider Session Cost should not be empty",
    })
    @IsString()
    sessionRate: string

    @IsOptional()
    @IsString()
    inNetworkCheck?: PayerCoverageCheckNetwork
}

export class RawCsvSmartScanInputDTO extends CommonContinuousMonitoringInputDTO {
    @IsNotEmpty({
        message: "Member ZIP should not be empty",
    })
    @IsString()
    @Matches(FRONT_REGEX_CONFIG.zip, {
        message: "Invalid ZIP code format, should be 5 digits",
    })
    zipCode: string
}

// Note: RawCsvDiscoverInputDTO does not extend RawCsvSmartScanInputDTO because we
// don't want RawCsvDiscoverInputDTO to be an `instanceof` RawCsvSmartScanInputDTO
export class RawCsvDiscoverInputDTO extends CommonContinuousMonitoringInputDTO {
    @IsNotEmpty({
        message: "Payer ID should not be empty",
    })
    @IsString()
    payer: string

    @IsNotEmpty({
        message: "Member ZIP should not be empty",
    })
    @IsString()
    @Matches(FRONT_REGEX_CONFIG.zip, {
        message: "Invalid ZIP code format, should be 5 digits",
    })
    zipCode: string
}

export class RawCsvGeneralInputDTO extends CommonContinuousMonitoringInputDTO {
    @IsNotEmpty({
        message: "Payer ID should not be empty",
    })
    @IsString()
    payer: string

    @IsNotEmpty({
        message: "Member ID should not be empty",
    })
    @IsString()
    memberId: string
}

export class RawCsvMedicaidInputDTO extends CommonProviderMemberDataDTO {
    @IsNotEmpty({
        message: "State code should not be empty",
    })
    @IsString()
    @IsIn(usaStates.map(item => item.abbreviation), {
        message: "Invalid state abbreviation",
    })
    state: string

    @IsOptional()
    @IsString()
    memberId?: string

    @IsOptional()
    // To handle empty strings in CSV
    @Transform(({ value }) => value || undefined)
    @IsEnum(LegalGender, {
        message: `Member Gender must be one of the following values: ${Object.values(
            LegalGender,
        ).join(", ")}`,
    })
    memberGender?: LegalGender

    @IsOptional()
    @IsString()
    cptCode?: string
}
